import { useCallback, useEffect, useMemo, useState } from "react";
import type { JobInfo } from "@/lib/api";
import { retainIds, selectableIds, toggleId } from "@/lib/selection";

/** Multi-select over the generations currently on screen. */
export function useJobSelection(jobs: JobInfo[]) {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());

  // Drop ids that scrolled out of scope (filter change, trash, other owner).
  // `retainIds` returns the same set when nothing changed, so React bails out.
  useEffect(() => {
    setIds((current) => retainIds(current, jobs));
  }, [jobs]);

  const toggle = useCallback((job: JobInfo) => setIds((current) => toggleId(current, job.id)), []);
  const clear = useCallback(() => setIds((current) => (current.size ? new Set() : current)), []);
  const selectAll = useCallback(() => setIds(new Set(selectableIds(jobs))), [jobs]);
  const selected = useMemo(() => jobs.filter((job) => ids.has(job.id)), [jobs, ids]);
  const selectableCount = useMemo(() => selectableIds(jobs).length, [jobs]);

  return { ids, selected, selectableCount, toggle, clear, selectAll };
}
